import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Card, Label, TextInput, Textarea } from "flowbite-react";
import { useForm, Controller } from "react-hook-form";
import { useGetData } from "../../../../../hooks/useGetData";
import { useMutationData } from "../../../../../hooks/useMutationData";
import { EvaluadoInterface } from "../../../../../interfaces/EvaluadoInterface";
import { Loading } from "../../../../../components/Loading/Loading";
import Header from "../../../../../components/HeaderForms/HeaderForms";
import { ThemeContext } from "../../../../../components/ThemeContext/ThemeContext";

export const VecinalesCrearScreen = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const tituloFormulario = "REFERENCIAS VECINALES";
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const { control, handleSubmit } = useForm();
  const [referenciasLaborales, setReferenciasLaborales] = useState([
    {
      name: "",
      address: "",
      phone: "",
      occupation: "",
      meet: "",
      comments: "",
      recommend: "",
      notes: "",
    },
  ]);
  const { evaluadosList_id } = useParams<{ evaluadosList_id: string }>();
  const [fechaNacimiento, setFechaNacimiento] = useState<string>("");
  const [edad, setEdad] = useState<number | null>(null);
  const { reset } = useForm();
  const [formData, setFormData] = useState({
    visitDate: "",
    name: "",
  });

  const calcularEdad = (fechaNacimiento: string) => {
    const fechaNac = new Date(fechaNacimiento);
    const hoy = new Date();
    let edadCalculada = hoy.getFullYear() - fechaNac.getFullYear();
    const mes = hoy.getMonth() - fechaNac.getMonth();

    if (mes < 0 || (mes === 0 && hoy.getDate() < fechaNac.getDate())) {
      edadCalculada--;
    }

    return edadCalculada;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleReferenciaChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setReferenciasLaborales((prev) =>
      prev.map((referencia, i) =>
        i === index ? { ...referencia, [name]: value } : referencia
      )
    );
  };

  const handleFechaNacimientoChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nuevaFechaNacimiento = event.target.value;
    setFechaNacimiento(nuevaFechaNacimiento);

    const nuevaEdad = calcularEdad(nuevaFechaNacimiento);
    setEdad(nuevaEdad);
  };

  const handlePrint = () => { 
    const printContent = document.getElementById("printableContent");

    if (printContent) {
      const printWindow = window.open("", "", "width=800,height=600");

      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Vista Previa de Impresión</title>
              <style>
                @media print {
                  body {
                    margin: 20px;
                    font-family: Arial, sans-serif;
                  }
                }
              </style>
            </head>
            <body>${printContent.innerHTML}</body>
          </html>
        `);

        const styles = document.styleSheets;
        for (let i = 0; i < styles.length; i++) {
          try {
            if (styles[i].cssRules) {
              const styleTag = printWindow.document.createElement("style");
              for (let j = 0; j < styles[i].cssRules.length; j++) {
                styleTag.appendChild(
                  printWindow.document.createTextNode(
                    styles[i].cssRules[j].cssText
                  )
                );
              }
              printWindow.document.head.appendChild(styleTag);
            }
          } catch (e) {
            console.warn("No se pudo copiar una hoja de estilo:", e);
          }
        }

        printWindow.document.close();
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            printWindow.close();
          }, 500);
        };
      } else {
        alert("No se pudo abrir la ventana de impresión.");
      }
    }
  };

  const renderHeader = () => (
    <>
      {/* Header */}
      <Header titulo={tituloFormulario} />
    </>
  );

  const queryEvaluado = useGetData<EvaluadoInterface>({
    url: `/evaluado/${evaluadosList_id}`,
    name: ["evaluado"],
  });

  const mutateFecha = useMutationData({
    url: `/evaluado/${evaluadosList_id}/fecha-visita-bienesinmuebles`,
    reload: ["evaluado", `${evaluadosList_id}`, "fecha-visita-bienesinmuebles"],
  });

  const mutateReferenciaVecinal = useMutationData({
    url: `/referencia_vecinal/${evaluadosList_id}`,
    reload: ["evaluado", "referencia_vecinal", `${evaluadosList_id}`],
  });

  const mutateNotasrv = useMutationData({
    url: `/notas_rv/${evaluadosList_id}`,
    reload: ["evaluado", "notas_rv", `${evaluadosList_id}`],
  });

  const generoMap: { [key: string]: string } = {
    M: "Masculino",
    F: "Femenino",
    O: "Otro",
  };

  const generoTexto =
    generoMap[queryEvaluado.data?.genero as string] || "No especificado";

  useEffect(() => {
    if (queryEvaluado.data) {
      const fechaNac = queryEvaluado.data.fecha_nacimiento || "";
      setFechaNacimiento(fechaNac);

      const nuevaEdad = calcularEdad(fechaNac);
      setEdad(nuevaEdad);

      reset({
        nombre: queryEvaluado.data.nombre || "",
        apellido_paterno: queryEvaluado.data.apellido_paterno || "",
        apellido_materno: queryEvaluado.data.apellido_materno || "",
        rfc: queryEvaluado.data.rfc || "",
        genero: queryEvaluado.data.genero || "",
        estado_civil: queryEvaluado.data.estado_civil || "",
        escolaridad: queryEvaluado.data.escolaridad || "",
        fecha_nacimiento: fechaNac,
        curp: queryEvaluado.data.curp || "",
        empresa: queryEvaluado.data.empresa?.nombre || "",
        puesto: queryEvaluado.data.puesto || "",
        fecha_ingreso: queryEvaluado.data.fecha_ingreso || "",
      });
    }
  }, [queryEvaluado.data, reset]);

  if (queryEvaluado.isLoading) {
    return <Loading />;
  }

  const renderPage1 = () => (
    <Card className={`mt-4 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
      <form className="flex flex-col gap-4">
        {/* Fecha Visita */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label htmlFor="fecha_refv" value="FECHA VISITA:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
            <Controller
              name="fecha_refv"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextInput
                  id="fecha_refv"
                  type="date"
                  placeholder="Seleccionar fecha"
                  {...field}
                  className={`w-32 ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-800 border-gray-300"}`}
                />
              )}
            />
          </div>
          {/* Campo para nombre */}
          <div className="col-span-2">
            <Label htmlFor="name" value="NOMBRE:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
            <TextInput
              id="name"
              name="name"
              value={`${queryEvaluado.data?.nombre || ""} ${
                queryEvaluado.data?.apellido_paterno || ""
              } ${queryEvaluado.data?.apellido_materno || ""}`}
              disabled={true}
              onChange={handleInputChange}
              className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
            />
          </div>
        </div>
        {/* Campo para RFC */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label htmlFor="rfc" value="RFC:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
            <TextInput
              id="rfc"
              name="rfc"
              value={queryEvaluado.data?.rfc || ""}
              disabled={true}
              onChange={handleInputChange}
              className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
            />
          </div>
          {/* Edad */}
          <div className="col-span-1">
            <Label htmlFor="edad">EDAD:</Label>
            <TextInput
              id="edad"
              name="edad"
              value={edad !== null ? edad.toString() : ""}
              disabled={true}
              className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
            />
          </div>
          {/* Género */}
          <div>
            <Label htmlFor="sex" value="SEXO:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
            <TextInput
              id="sex"
              name="sex"
              value={generoTexto}
              disabled={true}
              onChange={handleInputChange}
              className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
            />
          </div>
          {/* Estado Civil */}
          <div>
            <Label htmlFor="civilStatus" value="ESTADO CIVIL:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
            <TextInput
              id="civilStatus"
              name="civilStatus"
              value={queryEvaluado.data?.estado_civil || ""}
              disabled={true}
              onChange={handleInputChange}
              className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
            />
          </div>
          {/* Escolaridad */}
          <div>
            <Label htmlFor="education" value="ESCOLARIDAD:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
            <TextInput
              id="education"
              name="education"
              value={queryEvaluado.data?.escolaridad || ""}
              disabled={true}
              onChange={handleInputChange}
              className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
            />
          </div>
          {/* Fecha de Nacimiento */}
          <div>
            <Label htmlFor="birthDate" value="FECHA DE NACIMIENTO:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
            <TextInput
              id="birthDate"
              name="birthDate"
              type="date"
              value={queryEvaluado.data?.fecha_nacimiento || ""}
              disabled={true}
              onChange={handleInputChange}
              className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
            />
          </div>
          {/* CURP */}
          <div>
            <Label htmlFor="curp" value="CURP:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
            <TextInput
              id="curp"
              name="curp"
              value={queryEvaluado.data?.curp || ""}
              disabled={true}
              onChange={handleInputChange}
              className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
            />
          </div>
          {/* Empresa */}
          <div className="col-span-1">
            <Label htmlFor="empresa">EMPRESA:</Label>
            <TextInput
              id="empresa_id"
              name="empresa"
              value={queryEvaluado.data?.empresa_id?.nombre || ""}
              onChange={handleInputChange}
              disabled={true}
              className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
            />
          </div>
        </div>
      </form>
    </Card>
  );

  const renderPage2 = (index: number) => (
    <Card className={`mt-4 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
      <h2 className={`text-xl font-bold mb-4 p-2 ${isDarkMode ? "bg-blue-800 text-white" : "bg-customBlue text-white"}`}>
        REFERENCIA VECINAL {index + 1}{" "}
      </h2>
      <div className="space-y-4">
        <div className="flex flex-col text-left">
          <Label
            htmlFor={`nombre_rv${index + 1}`}
            value="NOMBRE:"
            className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
          />
          <Controller
            name={`nombre_rv${index + 1}`}
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextInput
                id={`nombre_rv${index + 1}`}
                type="text"
                placeholder="Nombre..."
                {...field}
                className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-800 border-gray-300"}`}
              />
            )}
          />
        </div>
        <div className="flex flex-col text-left">
          <Label
            htmlFor={`domicilio_rv${index + 1}`}
            value="DOMICILIO:"
            className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
          />
          <Controller
            name={`domicilio_rv${index + 1}`}
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextInput
                id={`domicilio_rv${index + 1}`}
                type="text"
                placeholder="Domicilio..."
                {...field}
                className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-800 border-gray-300"}`}
              />
            )}
          />
        </div>
        <div className="flex flex-col text-left">
          <Label
            htmlFor={`telefono_rv${index + 1}`}
            value="TELÉFONO:"
            className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
          />
          <Controller
            name={`telefono_rv${index + 1}`}
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextInput
                id={`telefono_rv${index + 1}`}
                type="text"
                placeholder="Telefono..."
                {...field}
                className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-800 border-gray-300"}`}
              />
            )}
          />
        </div>
        <div className="flex flex-col text-left">
          <Label
            htmlFor={`ocupacion_rv${index + 1}`}
            value="OCUPACIÓN:"
            className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
          />
          <Controller
            name={`ocupacion_rv${index + 1}`}
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextInput
                id={`ocupacion_rv${index + 1}`}
                type="text"
                placeholder="Ocupación..."
                {...field}
                className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-800 border-gray-300"}`}
              />
            )}
          />
        </div>
        <div className="flex flex-col text-left">
          <Label
            htmlFor={`anios_conocerlo_rv${index + 1}`}
            value="AÑOS DE CONOCER AL EVALUADO:"
            className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
          />
          <Controller
            name={`anios_conocerlo_rv${index + 1}`}
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextInput
                id={`anios_conocerlo_rv${index + 1}`}
                type="text"
                placeholder="Años..."
                {...field}
                className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-800 border-gray-300"}`}
              />
            )}
          />
        </div>
        <div className="flex flex-col text-left">
          <Label
            htmlFor={`comentarios_rv${index + 1}`}
            value="COMENTARIOS:"
            className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
          />
          <Controller
            name={`comentarios_rv${index + 1}`}
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextInput
                id={`comentarios_rv${index + 1}`}
                type="text"
                placeholder="Comentarios..."
                {...field}
                className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-800 border-gray-300"}`}
              />
            )}
          />
        </div>
        <div className="flex flex-col text-left">
          <Label
            htmlFor={`lo_recomienda_rv${index + 1}`}
            value="LO RECOMIENDA:"
            className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
          />
          <Controller
            name={`lo_recomienda_rv${index + 1}`}
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <select
                id={`lo_recomienda_rv${index + 1}`}
                {...field}
                onChange={(e) => field.onChange(e.target.value === "true")}
                value={field.value.toString()}
                className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-800 border-gray-300"}`}
              >
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            )}
          />
        </div>
      </div>
    </Card>
  );

  const addReferenciaLaboral = () => {
    if (referenciasLaborales.length < 2) {
      setReferenciasLaborales([
        ...referenciasLaborales,
        {
          name: "",
          address: "",
          phone: "",
          occupation: "",
          meet: "",
          comments: "",
          recommend: "",
          notes: "",
        },
      ]);
    } else {
      alert("Solo puedes agregar como maximo 3 referencias laborales.");
    }
  };

  const validateAndRemoveEmptyReferences = () => {
    setReferenciasLaborales((prev) =>
      prev.filter((referencia, index) => {
        if (index === 0) return true;
        const isFilled = Object.values(referencia).some(
          (value) => value.trim() !== ""
        );
        return isFilled;
      })
    );
  };

  const handlePageChange = (direction: "next" | "prev") => {
    validateAndRemoveEmptyReferences();
    if (direction === "next") {
      setCurrentPage((prev) => prev + 1);
    } else if (direction === "prev") {
      setCurrentPage((prev) => prev - 1);
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}>
      <div className={`container mx-auto p-4`}>
        <form
          onSubmit={handleSubmit((data) => {
            console.log(data);

            const formData = {
              ...data,
            };
            console.log("FormData:", formData);

            mutateFecha.mutate(
              {
                data: formData,
                tipo: "put",
              },
              {
                onSuccess: () => {
                  mutateReferenciaVecinal.mutate(
                    {
                      data: formData,
                      tipo: "post",
                    },
                    {
                      onSuccess: () => {
                        mutateNotasrv.mutate(
                          {
                            data: formData,
                            tipo: "post",
                          },
                          {
                            onSuccess: () => {
                              navigate(`/domiciliaria/${evaluadosList_id}`);
                            },
                          }
                        );
                      },
                    }
                  );
                },
              }
            );
          })}
          className={`max-w-4xl mx-auto rounded-lg shadow-lg overflow-hidden ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          {renderHeader()}
          {currentPage === 1 && renderPage1()}
          {currentPage === 2 && (
            <div>
              {referenciasLaborales.map((_, index) => (
                <div key={index} className={`referencia-${index}`}>
                  {renderPage2(index)}
                </div>
              ))}
              <h2 className={`text-xl font-bold mb-4 p-2 ${isDarkMode ? "bg-blue-800 text-white" : "bg-customBlue text-white"}`}>
                NOTAS
              </h2>
              <div className={`space-y-4 m-4 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
                <Controller
                  name="nota_rv"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Textarea
                      id="nota_rv"
                      {...field}
                      name="nota_rv"
                      rows={6}
                      onInput={(e) => {
                        const target = e.target as HTMLTextAreaElement;
                        target.style.height = "auto";
                        target.style.height = `${target.scrollHeight}px`;
                      }}
                      className={`resize-none ${isDarkMode ? "bg-gray-700 border-gray-600 text-gray-300" : "border-gray-300"}`}
                      style={{ height: "auto", overflow: "hidden" }}
                    />
                  )}
                />
              </div>
              <div className={`mt-4 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                <p className="text-left">
                  {" "}
                  Bajo protesta de decir verdad, hago constar que los datos
                  proporcionados son verdaderos.{" "}
                </p>
                <p className="text-left">
                  Asimismo, autorizo a la empresa SKA TRACKING AND SECURITY S.A.
                  de C.V. para que proporcione toda la Información, documentos y
                  todos los datos que derive de las evaluaciones de pruebas de
                  confianza que se me realicen.{" "}
                </p>
              </div>
              <div className="flex justify-center mt-4">
                <Button color="blue" onClick={addReferenciaLaboral}
                  className={`${isDarkMode ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-blue-500 text-white"}`}
                >
                  +
                </Button>
              </div>
            </div>
          )}
          {/* Botones de navegación */}
          <div className="flex justify-between mt-4">
            {currentPage < 2 && (
              <Button
                color="red"
                onClick={() => {
                  window.scrollTo(0, 0);
                  navigate(`/domiciliaria/${evaluadosList_id}`);
                }}
                className={`${isDarkMode ? "bg-red-600 hover:bg-red-700 text-white" : "bg-red-500 text-white"}`}
              >
                Cancelar
              </Button>
            )}
            {currentPage > 1 && (
              <Button
                onClick={() => {
                  window.scrollTo(0, 0);
                  handlePageChange("prev");
                }}
                className={`${isDarkMode ? "bg-violet-600 hover:bg-violet-900 text-white" : "bg-violet-400 text-white"}`}
              >
                Anterior
              </Button>
            )}
            {currentPage < 2 ? (
              <Button
                color="blue"
                onClick={() => {
                  window.scrollTo(0, 0);
                  handlePageChange("next");
                }}
                className={`${isDarkMode ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-blue-500 text-white"}`}
              >
                Siguiente
              </Button>
            ) : (
              <div className="flex space-x-4">
                <button
                  type="submit"
                  className={`rounded px-4 py-2 ${isDarkMode ? "bg-green-600 hover:bg-green-700 text-white" : "bg-green-500 text-white"}`}
                  onClick={validateAndRemoveEmptyReferences}
                >
                  Guardar
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};
